import { useEffect, useState } from "react";
import { Form, Input, InputNumber, Switch } from "antd";
import LoadableButton from "../../components/button/LoadableButton";
import ModalComponent from "../../components/global/modals/ModalComponent";
import {
  AddPaymentTypeApi,
  ChangePaymentTypeStatusApi,
  DeletePaymentTypeApi,
  EditPaymentTypeApi,
  GetPaymentTypeApi,
} from "../../api/request/paymentType";
import moment from "moment";
import toast from "react-hot-toast";
import Plus from "../../assets/Plus";
import SkeletonTable from "../../components/table/SkeletonTable";
import paymentTypeColumns from "../../columns/PaymentType/PaymentType";
import Edit from "../../assets/Edit";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import Delete from "../../assets/Delete";
import ConfirmationModal from "../../components/global/modals/ConfirmationModal";

const PaymentType = () => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const [isStatusLoading, setIsStatusLoading] = useState(false);

  const [selectedToggleItem, setSelectedToggleItem] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);

  const fetchPaymentTypes = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetPaymentTypeApi();
      if (data?.status) {
        setData(
          data?.data?.map((item) => {
            return {
              ...item,
              created_at: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "--",
              updated_at: item?.updated_at
                ? moment(item?.updated_at).format("lll")
                : "--",
              status: (
                <Switch
                  checked={item.status === "active"}
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  onChange={(checked) => {
                    setSelectedToggleItem(item);
                    setPendingStatus(checked ? "active" : "inactive");
                    setIsStatusConfirmOpen(true);
                  }}
                />
              ),
              actions: (
                <div className="flex gap-2">
                  <GlobalTooltip title="Edit Payment Type">
                    <button onClick={() => openEditModal(item)}>
                      <Edit />
                    </button>
                  </GlobalTooltip>
                  {/* <GlobalTooltip title="Delete Payment Type">
                    <button
                      disabled={isDeleteLoading}
                      onClick={() => {
                        setSelectedPlan(item);
                        setIsDeleteOpen(true);
                      }}
                    >
                      <Delete />
                    </button>
                  </GlobalTooltip> */}
                </div>
              ),
            };
          }),
        );
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const payload = {
        ...values,
      };

      if (editingPlan) {
        await EditPaymentTypeApi({ id: editingPlan?.id, ...payload });
      } else {
        await AddPaymentTypeApi(payload);
      }

      fetchPaymentTypes();
      setIsModalOpen(false);
      form.resetFields();
      setEditingPlan(null);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedPlan) return;
    setIsDeleteLoading(true);
    try {
      const { data } = await DeletePaymentTypeApi({ id: selectedPlan?.id });
      if (data?.status) {
        fetchPaymentTypes();
        setIsDeleteOpen(false);
        setSelectedPlan(null);
        toast.success(data?.message || "Payment Type deleted");
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedToggleItem || !pendingStatus) return;
    setIsStatusLoading(true);
    try {
      const payload = {
        id: selectedToggleItem.id,
        status: pendingStatus,
      };
      const { data } = await ChangePaymentTypeStatusApi(payload);
      if (data?.status) {
        fetchPaymentTypes();
        toast.success(data?.message || "Status updated");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Update failed");
    } finally {
      setIsStatusConfirmOpen(false);
      setSelectedToggleItem(null);
      setPendingStatus(null);
      setIsStatusLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingPlan(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      type_name: plan?.type_name,
    });
    setIsModalOpen(true);
  };

  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  return (
    <>
      <div className="border bg-white rounded-xl py-3">
        <div className="flex items-center justify-between sm:px-4 max-sm:px-2 mb-3">
          <h2 className="text-xl font-semibold">Payment Type</h2>
          <LoadableButton onClick={openAddModal}>
            <Plus className="w-5 h-5" />
            <span className="max-sm:text-xs">Add Payment Type</span>
          </LoadableButton>
        </div>

        <SkeletonTable
          columns={paymentTypeColumns}
          data={data}
          isLoading={isLoading}
          pagination={true}
        />
      </div>

      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={editingPlan ? "Edit Payment Type" : "Add Payment Type"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: true }}
        >
          {/* <div className="grid grid-cols-2 gap-4"> */}
          <Form.Item
            label="Payment Type Name"
            name="type_name"
            rules={[
              { required: true, message: "Please enter payment type name" },
            ]}
          >
            <Input className="w-full" placeholder="Enter payment type name" />
          </Form.Item>
          {/* </div> */}

          <div className="flex justify-center gap-2 mt-4">
            <LoadableButton
              className="btn-primary"
              type="submit"
              lable={editingPlan ? "Update" : "Add"}
              loadingLable="Saving..."
              isLoading={isLoading}
            />
          </div>
        </Form>
      </ModalComponent>

      <ConfirmationModal
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        type="error"
        message={`Are you sure you want to delete this payment type?`}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting..."
      />

      <ConfirmationModal
        isOpen={isStatusConfirmOpen}
        setIsOpen={setIsStatusConfirmOpen}
        type="info"
        message={`Are you sure you want to ${
          pendingStatus === "active" ? "activate" : "deactivate"
        } this Payment Type?`}
        onConfirm={handleToggleStatus}
        isLoading={isStatusLoading}
        loadingLabel="Updating..."
      />
    </>
  );
};

export default PaymentType;
